// PMax Audience Signals — Audience Signals section, deterministic check.
// Per-asset-group audience-signal coverage, groups with no signal first. Signals shown by name.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';

interface AssetGroupRow {
  id?: string;
  name: string;
  campaignName: string;
  audienceSignalCount: number;
  audienceSignals: string[];
}

interface AudienceSignalsShape {
  totalAssetGroups: number;
  withoutSignalCount: number;
  assetGroups: AssetGroupRow[];
}

export const PmaxAudienceSignalsEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as AudienceSignalsShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Audience signal coverage
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalAssetGroups} asset groups
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.totalAssetGroups.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Asset groups</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.withoutSignalCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>No audience signal</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Per-asset-group signals
          <span className='evidence__section-tag evidence__section-tag--context'>
            weakest first
          </span>
        </h4>
        <table className='evidence__table evidence__table--pmax-signals'>
          <thead>
            <tr>
              <th>Asset group</th>
              <th>Campaign</th>
              <th>Audience signals</th>
            </tr>
          </thead>
          <tbody>
            {e.assetGroups.map(g => (
              <tr
                key={g.id ?? g.name}
                className={classNames({ 'evidence__row--flagged': g.audienceSignalCount === 0 })}
              >
                <td>{g.name}</td>
                <td>{g.campaignName}</td>
                <td>
                  {g.audienceSignalCount === 0 ? (
                    <span className='evidence__inline-note'>No audience signal</span>
                  ) : (
                    <span className='evidence__modifier-list'>
                      {g.audienceSignals.map((s, i) => (
                        <span key={`${s}-${i}`} className='evidence__audience-chip'>
                          {s}
                        </span>
                      ))}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        Audience signals are hints that inform Smart Bidding and speed the learning period — they
        don't target or exclude. Tinuiti's most impactful signals are first-party (Customer Match,
        remarketing) and Custom Audiences. Every active asset group should carry at least one.
      </p>
    </div>
  );
};
